/**
 * @file src/utils/timezone.js
 * @description Compare a screen's OS clock against its venue's timezone.
 *
 * WHY THIS EXISTS
 * The Android player computes its own schedule alarms from its own clock —
 * `Utils.getCampaignNextStartDateEpochSeconds` resolves the venue wall-clock
 * `start_time` against `LocalDateTime.now()` / `ZoneOffset.systemDefault()`.
 * That is deliberate: it works offline and needs no feed to know the time.
 *
 * The cost is that a screen whose OS clock is set to the wrong zone runs EVERY
 * schedule at the wrong time, forever, with nothing to show for it. So instead
 * of forcing a timezone into the player, we report the device's zone
 * (device_info.timezone, player v313+) and make the disagreement visible here.
 *
 * COMPARE OFFSETS, NOT NAMES
 * `Asia/Kuwait` and `Asia/Riyadh` are different strings and identical clocks
 * (both UTC+3). A name comparison would warn on pairs that schedule the same,
 * training people to ignore the warning. We compare the CURRENT UTC offset, so
 * we only warn when the wall clock genuinely differs — and comparing "current"
 * rather than "standard" offset means a DST mismatch is caught too.
 */

/** Current UTC offset of an IANA zone, in minutes. null if unparseable. */
export function tzOffsetMinutes(zone, at = new Date()) {
    if (!zone) return null;
    try {
        // 'shortOffset' yields "GMT+3", "GMT+5:45", or bare "GMT" at UTC.
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'shortOffset',
        }).formatToParts(at);
        const label = parts.find((p) => p.type === 'timeZoneName')?.value || '';
        const m = label.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
        if (!m) return label === 'GMT' ? 0 : null;
        const sign = m[1] === '-' ? -1 : 1;
        return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
    } catch {
        return null; // not a valid IANA zone
    }
}

/** "UTC+2", "UTC-5:30", "UTC" — for display next to a zone name. */
export function tzOffsetLabel(zone) {
    const min = tzOffsetMinutes(zone);
    if (min === null) return '';
    if (min === 0) return 'UTC';
    const sign = min < 0 ? '-' : '+';
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/**
 * Compare a device's reported OS zone against its venue's resolved zone.
 *
 * @returns {null | {kind: 'mismatch'|'unknown', deltaMinutes?: number, ...}}
 *   null      — offsets agree (or agree closely enough to schedule identically)
 *   'unknown' — device hasn't reported a zone (player older than v313), or it
 *               reported something we can't parse. NOT a mismatch: an old
 *               player must not light up the whole fleet red.
 *   'mismatch'— the clocks genuinely differ; deltaMinutes = device − venue
 *               (positive = device runs ahead, so schedules fire EARLY).
 */
export function tzMismatch(deviceZone, venueZone) {
    if (!venueZone) return null;                       // nothing to compare against
    if (!deviceZone) return { kind: 'unknown', deviceZone: null, venueZone };

    const dev = tzOffsetMinutes(deviceZone);
    const ven = tzOffsetMinutes(venueZone);
    if (dev === null || ven === null) {
        return { kind: 'unknown', deviceZone, venueZone };
    }
    if (dev === ven) return null;                      // e.g. Asia/Kuwait vs Asia/Riyadh

    return {
        kind: 'mismatch',
        deviceZone,
        venueZone,
        deltaMinutes: dev - ven,
        deviceOffsetLabel: tzOffsetLabel(deviceZone),
        venueOffsetLabel: tzOffsetLabel(venueZone),
    };
}

/** "1 hour early" / "30 minutes late" — how the schedule actually misbehaves. */
export function tzDriftPhrase(deltaMinutes, t) {
    const ahead = deltaMinutes > 0;
    const abs = Math.abs(deltaMinutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const amount = h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
    // Device ahead of the venue => its "12:00" arrives before the venue's.
    return t
        ? t(ahead ? 'devices.tz_drift_early' : 'devices.tz_drift_late', { amount })
        : `${amount} ${ahead ? 'early' : 'late'}`;
}

/**
 * "H:i" (24h, e.g. "08:00") -> "8:00 ص" / "8:00 م" — Latin digits, literal
 * ص/م (not locale-switched to AM/PM), matching the presence surface's own
 * convention (see Grouping/DeviceCard.jsx's scheduled_off resume-time and
 * .claude/pp-presence-scheduled-off.html). Used for both device_local_time
 * and branch_local_time, which the backend sends pre-formatted "H:i".
 */
export function formatWallClock12h(raw) {
    const m = typeof raw === 'string' ? raw.match(/^([01]\d|2[0-3]):([0-5]\d)$/) : null;
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const period = h < 12 ? 'ص' : 'م';
    h = h % 12 || 12;
    return `${h}:${m[2]} ${period}`;
}

/**
 * Readable Arabic magnitude for a device/branch clock drift, e.g. for the
 * amber "ساعة الصندوق متأخّرة/متقدّمة {..} عن توقيت الفرع" strip.
 * absMinutes is the unsigned delta; direction is the caller's concern (sign
 * of offset_delta_minutes). Named buckets for the common whole/half-hour
 * timezone-offset cases; anything else falls back to a generic count —
 * minutes below an hour (e.g. the 30/45-minute zones), else rounded hours.
 */
export function arabicDeltaPhrase(absMinutes) {
    if (absMinutes === 60) return 'ساعة';
    if (absMinutes === 120) return 'ساعتين';
    if (absMinutes === 90) return 'ساعة ونصف';
    if (absMinutes < 60) return `${absMinutes} دقيقة`;
    return `${Math.round(absMinutes / 60)} ساعات`;
}
